import type { CartUI, ItemUI, PaymentMethod } from '@libs/common-node';
import { PaymentMethodsType } from '@libs/core';
import { formatPriceFraction } from './formatPriceFraction';

type PaymentMethodField = keyof PaymentMethod;
type ItemField = keyof ItemUI;

export const applyFormatPriceFraction = (cart: CartUI): CartUI => {
    type SummaryField = Exclude<keyof typeof cart.summary, 'taxAmounts' | 'dealsAndOffers'>;
    type TaxField = keyof typeof cart.summary.taxAmounts;

    // Deep clone to avoid mutating the original cart object
    const cartClone: CartUI = JSON.parse(JSON.stringify(cart));
    const { fractionDigits } = cartClone;

    const formatTaxFields = () => {
        const taxMoneyFields: TaxField[] = ['total', 'ivu', 'ivu2', 'gst', 'hst', 'pst'];

        taxMoneyFields.forEach(field => {
            cartClone.summary.taxAmounts[field] = formatPriceFraction(
                cartClone.summary.taxAmounts[field],
                fractionDigits
            );
        });
    };

    const formatSummary = () => {
        const summaryMoneyFields: SummaryField[] = [
            'totalAmount',
            'subtotalAmount',
            'shippingCost',
            'donationAmount',
            'loyaltyAmount',
            'savingsAmount',
            'deliveryTip',
        ];

        if (cartClone.summary) {
            summaryMoneyFields.forEach(field => {
                cartClone.summary[field] = formatPriceFraction(cartClone.summary[field], fractionDigits);
            });

            formatTaxFields();
            formatDealsAndOffers();
        }
    };

    const formatDealsAndOffers = () => {
        if (cartClone?.summary?.dealsAndOffers) {
            cartClone.summary.dealsAndOffers.forEach(promo => {
                promo.discountValue = formatPriceFraction(promo.discountValue, fractionDigits);
            });
        }
    };

    const formatPaymentMethods = () => {
        const paymentMethodMoneyFields: PaymentMethodField[] = ['amount', 'giftCardAmount'];
        if (cartClone.paymentMethods) {
            cartClone.paymentMethods.forEach(paymentMethod => {
                const isGiftCard = paymentMethod.method === PaymentMethodsType.GIFTCARD;

                if (isGiftCard) {
                    paymentMethodMoneyFields.forEach(field => {
                        if (typeof paymentMethod[field] === 'number') {
                            const price = paymentMethod[field] as number;

                            (paymentMethod[field] as number) = formatPriceFraction(price, fractionDigits);
                        }
                    });
                }
            });
        }
    };

    const formatFulfillmentGroups = () => {
        const cartItemPriceFields: ItemField[] = [
            'basePrice',
            'salePrice',
            'totalPrice',
            'listPrice',
            'taxAmount',
            'mapPriceCentAmount',
        ];

        cartClone.fulfillmentGroups?.forEach(group => {
            group.items.forEach(item => {
                cartItemPriceFields.forEach(field => {
                    const itemValue = item[field];
                    if (typeof itemValue === 'number') {
                        (item[field] as number) = formatPriceFraction(itemValue, fractionDigits);
                    }
                });
            });
        });
    };

    formatSummary();
    formatPaymentMethods();
    formatFulfillmentGroups();

    return cartClone;
};
